// Tipo de letra elegible por el usuario (2026-09-08, tercera fila de
// TemaSelector, mismo día que los cuatro temas nuevos; "Como el logo" sumada
// el 2026-09-10).
//
// Por qué un catálogo CERRADO de 5 pares (sans para la UI + serif para
// títulos y la voz que enseña) y no una fuente libre: cualquier combinación
// que el usuario elija sigue siendo legible y coherente con el resto del
// sistema (mismo criterio de `tema.js`).
//
// Por qué Google Fonts: es el único proveedor de fuentes externas que la
// CSP de `vercel.json` permite hoy (`style-src` fonts.googleapis.com,
// `font-src` fonts.gstatic.com); no se agrega otro proveedor. Excepción:
// las letras con `google: null` están AUTO-HOSPEDADAS en public/fonts/ (hoy
// solo "Como el logo" = Baloo 2, la misma fuente del header) y no dependen
// de un tercero; sus @font-face ya viven en public/index.html.
//
// Por qué la fuente se descarga SOLO cuando se elige: el default ('mipae')
// ya viene cargado en public/index.html (Inter + Fraunces); pedirle a todo
// el mundo que descargue familias de más solo por abrir el selector sería
// peso muerto para la inmensa mayoría que nunca cambia la letra. Las
// auto-hospedadas siguen la misma regla: el navegador recién las pide
// cuando el CSS las usa, no hay un link que dispare la descarga.
//
// Por qué el default no escribe ninguna variable: igual que tema.js, sin
// elección Tailwind cae directo a Inter/Fraunces (tailwind.config.js), sin
// pasar por este módulo.
//
// Por qué el link inyectado NUNCA se remueve: una fuente ya descargada no
// cuesta nada mantenerla, y quitarla causaría un parpadeo (FOUT) si el
// usuario vuelve a elegirla más tarde.

export const LETRA_DEFAULT = 'mipae';
export const CLAVE_LETRA = 'mipae_letra';

export const LETRAS = [
  { id: 'mipae', nombre: 'MiPAE', sans: 'Inter', serif: 'Fraunces', google: null },
  {
    id: 'redonda',
    nombre: 'Redonda',
    sans: 'Nunito',
    serif: 'Fraunces',
    google: 'family=Nunito:wght@400;500;600;700',
  },
  {
    id: 'clasica',
    nombre: 'Clásica',
    sans: 'Source Sans 3',
    serif: 'Source Serif 4',
    google:
      'family=Source+Sans+3:wght@400;500;600;700&family=Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;1,8..60,400',
  },
  {
    id: 'legible',
    nombre: 'Legible',
    // Atkinson Hyperlegible: fuente diseñada para baja visión. Solo trae
    // 400 y 700 (el navegador aproxima 500/600 con negrita sintética).
    sans: 'Atkinson Hyperlegible',
    serif: 'Fraunces',
    google: 'family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400',
  },
  {
    // Baloo 2 en TODO (texto, títulos y el logo del Home): la letra del
    // wordmark del header llevada a la app entera, pedida por el fundador el
    // 2026-09-10 tras ver la maqueta ("me encanta"). Auto-hospedada en
    // public/fonts/ (google: null), como el header: la marca no depende de
    // un tercero. Entra como opción y no como default hasta usarla en turno.
    id: 'baloo',
    nombre: 'Como el logo',
    sans: 'Baloo 2',
    serif: 'Baloo 2',
    google: null,
  },
];

export function letraHabilitada() {
  return process.env.REACT_APP_LETRA_ENABLED !== 'false';
}

export function letraPorId(id) {
  return LETRAS.find((l) => l.id === id) || null;
}

// jsonb/localStorage adulterado jamás rompe: cualquier valor fuera del
// catálogo cae al default.
export function normalizarLetra(id) {
  return letraPorId(id) ? id : LETRA_DEFAULT;
}

// "Nunito" → '"Nunito"' (comillas dobles): un nombre con espacios necesita
// comillas para ser un valor CSS de font-family válido.
export function valorFuente(nombre) {
  return `"${nombre}"`;
}

// Con el default (o un id sin `google`) no hay nada que descargar.
export function urlGoogleFonts(id) {
  const normalizado = normalizarLetra(id);
  const entrada = letraPorId(normalizado);
  if (!entrada || !entrada.google) return null;
  return `https://fonts.googleapis.com/css2?${entrada.google}&display=swap`;
}

export function leerLetra() {
  if (!letraHabilitada()) return LETRA_DEFAULT;
  try {
    return normalizarLetra(localStorage.getItem(CLAVE_LETRA));
  } catch {
    return LETRA_DEFAULT;
  }
}

export function guardarLetra(id) {
  const normalizado = normalizarLetra(id);
  try {
    localStorage.setItem(CLAVE_LETRA, normalizado);
  } catch { /* noop: localStorage caído no debe romper el ajuste */ }
  return normalizado;
}

// Aplica (o limpia) las variables --fuente-sans/--fuente-serif sobre <html>
// y asegura el <link> de Google Fonts de la letra elegida. Con el default o
// el flag apagado, limpia las variables (Tailwind cae a Inter/Fraunces) pero
// NUNCA quita un <link> ya insertado (ver el porqué arriba).
export function aplicarLetraEnDocumento(id, { root, doc } = {}) {
  try {
    const elemento = root || document.documentElement;
    const documento = doc || document;
    const normalizado = letraHabilitada() ? normalizarLetra(id) : LETRA_DEFAULT;
    const entrada = letraPorId(normalizado);

    if (normalizado === LETRA_DEFAULT) {
      elemento.style.removeProperty('--fuente-sans');
      elemento.style.removeProperty('--fuente-serif');
      return null;
    }

    elemento.style.setProperty('--fuente-sans', valorFuente(entrada.sans));
    elemento.style.setProperty('--fuente-serif', valorFuente(entrada.serif));

    const idLink = `mipae-fuente-${normalizado}`;
    const url = urlGoogleFonts(normalizado);
    if (url && !documento.getElementById(idLink)) {
      const link = documento.createElement('link');
      link.id = idLink;
      link.rel = 'stylesheet';
      link.href = url;
      documento.head.appendChild(link);
    }

    return entrada;
  } catch {
    return null;
  }
}
